import { MouseEvent, PropsWithChildren, ReactNode, useCallback, useRef } from 'react';
import { Subtitle } from '@/wrappers/Drawer/Subtitle';
import { DialogActions } from './Actions';
import { DialogContainer, ICallableRef, IDialogContainerProps } from './Container';
import { DialogContent } from './Content';
import { DialogOpenButton } from './OpenButton';
import { DialogTitle } from './Title';

type IBaseProps = Pick<IDialogContainerProps, 'fullWidth' | 'maxWidth'> & {
    name: string;
    open?: boolean;
    title?: ReactNode;
    subTitle?: ReactNode;
    actions?: (onClose: () => void) => ReactNode;
    openButtonValue: ReactNode;
    openButton: (onClick: (event?: MouseEvent) => void) => ReactNode;
};
type IPropsWithOpenButton = Omit<IBaseProps, 'openButtonValue'> & {
    openButtonValue?: IBaseProps['openButtonValue'];
};
type IDefaultProps = Omit<IBaseProps, 'openButton'> & {
    openButton?: IBaseProps['openButton'];
};

export type IDialogProps = IDefaultProps | IPropsWithOpenButton;

const Dialog = (props: PropsWithChildren<IDialogProps>) => {
    const containerRef = useRef<ICallableRef>(null);
    const actions = props.actions || (() => null);
    const handleClose = useCallback(() => {
        if (containerRef.current) {
            containerRef.current?.setOpen(false);
        }
    }, []);
    const handleOpen = useCallback(() => {
        if (containerRef.current) {
            containerRef.current?.setOpen(true);
        }
    }, []);

    return (
        <>
            <DialogOpenButton
                render={props.openButton}
                onClick={handleOpen}
                value={props.openButtonValue}
                name={props.name}
            />
            <DialogContainer
                innerRef={containerRef}
                onClick={(event) => {
                    event.stopPropagation();
                }}
                open={props.open ?? false}
                maxWidth={props.maxWidth}
                fullWidth={props.fullWidth}
                data-testid={`${props.name}-dialog`}
                onClose={handleClose}
            >
                <DialogTitle onClose={handleClose} subTitle={props.subTitle}>
                    {props.title}
                </DialogTitle>
                <DialogContent>{props.children}</DialogContent>
                <DialogActions>{actions(handleClose)}</DialogActions>
            </DialogContainer>
        </>
    );
};

const Wrapper = Object.assign(Dialog, {
    Actions: DialogActions,
    Container: DialogContainer,
    Content: DialogContent,
    OpenButton: DialogOpenButton,
    Subtitle: Subtitle,
    Title: DialogTitle
});

export { Wrapper as Dialog };
